import type { Position } from 'geojson';

import type {
  FieldDefinition,
  FieldValueType,
  GeometrySummary,
  JsonValue,
  SpatialExtent,
  SupportedGeometry,
  SupportedGeometryType,
  VectorFeatureCollection,
} from '../model';

const GEOMETRY_TYPES: readonly SupportedGeometryType[] = [
  'Point',
  'MultiPoint',
  'LineString',
  'MultiLineString',
  'Polygon',
  'MultiPolygon',
];

interface MutableFieldInspection {
  readonly name: string;
  type: FieldValueType;
  presentCount: number;
  nullCount: number;
}

function valueType(value: JsonValue): Exclude<FieldValueType, 'mixed' | 'unknown'> | 'unknown' {
  if (value === null) return 'unknown';
  if (Array.isArray(value)) return 'array';

  switch (typeof value) {
    case 'boolean':
      return 'boolean';
    case 'number':
      return Number.isInteger(value) ? 'integer' : 'number';
    case 'string':
      return 'string';
    case 'object':
      return 'object';
  }
}

function mergeValueTypes(current: FieldValueType, next: FieldValueType): FieldValueType {
  if (next === 'unknown') return current;
  if (current === 'unknown' || current === next) return next;

  if (
    (current === 'integer' && next === 'number') ||
    (current === 'number' && next === 'integer')
  ) {
    return 'number';
  }

  return 'mixed';
}

export function discoverFields(collection: VectorFeatureCollection): readonly FieldDefinition[] {
  const discovered = new Map<string, MutableFieldInspection>();

  for (const feature of collection.features) {
    if (!feature.properties) continue;

    for (const [name, value] of Object.entries(feature.properties)) {
      const existing = discovered.get(name) ?? {
        name,
        type: 'unknown' as const,
        presentCount: 0,
        nullCount: 0,
      };

      existing.presentCount += 1;
      if (value === null) existing.nullCount += 1;
      existing.type = mergeValueTypes(existing.type, valueType(value));
      discovered.set(name, existing);
    }
  }

  return [...discovered.values()].map((field) => ({
    name: field.name,
    type: field.type,
    nullable: field.nullCount > 0 || field.presentCount < collection.features.length,
    presentCount: field.presentCount,
    nullCount: field.nullCount,
  }));
}

function visitGeometryPositions(
  geometry: SupportedGeometry,
  visit: (position: Position) => void,
): void {
  switch (geometry.type) {
    case 'Point':
      visit(geometry.coordinates);
      return;
    case 'MultiPoint':
    case 'LineString':
      geometry.coordinates.forEach(visit);
      return;
    case 'MultiLineString':
    case 'Polygon':
      geometry.coordinates.forEach((line) => line.forEach(visit));
      return;
    case 'MultiPolygon':
      geometry.coordinates.forEach((polygon) => polygon.forEach((ring) => ring.forEach(visit)));
  }
}

export interface GeoJsonInspection {
  readonly fields: readonly FieldDefinition[];
  readonly geometry: GeometrySummary;
  readonly extent: SpatialExtent | null;
}

export function inspectGeoJson(collection: VectorFeatureCollection): GeoJsonInspection {
  const counts: Record<SupportedGeometryType, number> = {
    Point: 0,
    MultiPoint: 0,
    LineString: 0,
    MultiLineString: 0,
    Polygon: 0,
    MultiPolygon: 0,
  };
  let nullGeometryCount = 0;
  let emptyGeometryCount = 0;
  let renderableFeatureCount = 0;
  let rawWest = Number.POSITIVE_INFINITY;
  let rawEast = Number.NEGATIVE_INFINITY;
  let shiftedWest = Number.POSITIVE_INFINITY;
  let shiftedEast = Number.NEGATIVE_INFINITY;
  let south = Number.POSITIVE_INFINITY;
  let north = Number.NEGATIVE_INFINITY;

  for (const feature of collection.features) {
    const geometry = feature.geometry;
    if (!geometry) {
      nullGeometryCount += 1;
      continue;
    }

    counts[geometry.type] += 1;
    let hasCoordinate = false;
    visitGeometryPositions(geometry, (position) => {
      const longitude = position[0];
      const latitude = position[1];
      if (longitude === undefined || latitude === undefined) return;

      hasCoordinate = true;
      rawWest = Math.min(rawWest, longitude);
      rawEast = Math.max(rawEast, longitude);

      const shiftedLongitude = longitude < 0 ? longitude + 360 : longitude;
      shiftedWest = Math.min(shiftedWest, shiftedLongitude);
      shiftedEast = Math.max(shiftedEast, shiftedLongitude);
      south = Math.min(south, latitude);
      north = Math.max(north, latitude);
    });

    if (hasCoordinate) renderableFeatureCount += 1;
    else emptyGeometryCount += 1;
  }

  const types = GEOMETRY_TYPES.filter((type) => counts[type] > 0);
  const geometry: GeometrySummary = {
    types,
    counts,
    isMixed: types.length > 1,
    renderableFeatureCount,
    nullGeometryCount,
    emptyGeometryCount,
  };

  if (renderableFeatureCount === 0) {
    return { fields: discoverFields(collection), geometry, extent: null };
  }

  const rawWidth = rawEast - rawWest;
  const shiftedWidth = shiftedEast - shiftedWest;
  const useAntimeridianExtent = shiftedWidth < rawWidth;

  return {
    fields: discoverFields(collection),
    geometry,
    extent: {
      west: useAntimeridianExtent ? shiftedWest : rawWest,
      south,
      east: useAntimeridianExtent ? shiftedEast : rawEast,
      north,
      crossesAntimeridian: useAntimeridianExtent,
    },
  };
}

export function describeGeometry(summary: GeometrySummary): string {
  if (summary.types.length === 0) return 'No renderable geometry';

  const parts = summary.types.map((type) => `${type} (${summary.counts[type]})`);
  return summary.isMixed ? `Mixed: ${parts.join(', ')}` : (parts[0] ?? 'No renderable geometry');
}
